import { ref } from 'vue';
import { useRouter } from 'vue-router';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: string;
  duration?: number;
  actions?: Array<{
    label: string;
    action: () => void;
    variant?: 'primary' | 'secondary';
  }>;
}

const notifications = ref<Notification[]>([]);
let notificationId = 0;

export function useNotifications() {
  const router = useRouter();

  const addNotification = (notification: Omit<Notification, 'id'>): string => {
    const id = `notification-${++notificationId}`;
    const newNotification: Notification = {
      id,
      duration: 5000, // Default 5 seconds
      ...notification,
    };

    notifications.value.push(newNotification);

    // Auto-remove after duration
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  };

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex(n => n.id === id);
    if (index > -1) {
      notifications.value.splice(index, 1);
    }
  };

  const clearAll = () => {
    notifications.value.length = 0;
  };

  // Convenience methods
  const showSuccess = (title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: 'success',
      title,
      message,
      ...options,
    });
  };

  const showError = (title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: 'error',
      title,
      message,
      duration: 0, // Don't auto-dismiss errors
      ...options,
    });
  };

  const showInfo = (title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: 'info',
      title,
      message,
      ...options,
    });
  };

  const showWarning = (title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: 'warning',
      title,
      message,
      ...options,
    });
  };

  // Task completion success with navigation
  const showTaskCompletionSuccess = (taskTitle?: string, redirectPath = '/dashboard/sites') => {
    const notificationId = addNotification({
      type: 'success',
      title: '🎉 Task Completed Successfully!',
      message: taskTitle ? `"${taskTitle}" has been completed.` : 'Your task has been completed.',
      duration: 3000,
      actions: [
        {
          label: 'Finish',
          action: () => {
            // Clear activeTask and redirect
            clearActiveTaskAndRedirect(redirectPath);
          },
          variant: 'primary'
        }
      ]
    });

    // Auto-redirect when toast expires
    setTimeout(() => {
      clearActiveTaskAndRedirect(redirectPath);
    }, 3000);

    return notificationId;
  };
  
  // Helper function to clear activeTask and redirect
  const clearActiveTaskAndRedirect = async (redirectPath: string) => {
    const { useTasksStore } = await import('@/stores/v2');
    const { TasksStorageService } = await import('@/services/v2/tasks/storage');
    
    const tasksStore = useTasksStore();
    // Clear activeTask from store and storage
    tasksStore.activeTask = null;
    await TasksStorageService.storeActiveTask(null as any);
    
    router.push(redirectPath);
  };

  return {
    // State
    notifications: notifications.value,
    
    // Methods
    addNotification,
    removeNotification,
    clearAll,
    
    // Convenience methods
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showTaskCompletionSuccess,
  };
} 